import {
  ApiConditionCustom,
  ApiConditionGroup,
  ApiOperator,
  ApiSessionConditionCustom,
} from "@incendium/api";
import {
  Box,
  GridSize,
  InputAdornment,
  MenuItem,
  TextField,
} from "@mui/material";
import { ColsCol, ColsLayout } from "Components/ColsLayout/ColsLayout";
import ConditionalGroup from "Components/ConditionalGroup/ConditionalGroup";
import { useConditionGroupContext } from "Components/ConditionalGroup/ConditionGroupProvider";
import ConversionsDropdown from "Components/Conversions/ConversionsDropdown";
import { engagementRuleType } from "features/leadScore/components/EngagementRules";
import { formatEnumVal } from "Helpers/enumToText";
import produce from "immer";
import { useMemo, useState } from "react";
import { useEffect } from "react";

const defaultColWidth = [4, 4, 4];

const Row = ({
  groupIdx,
  rowIdx,
  types,
}: {
  groupIdx: number;
  rowIdx: number;
  types: string[];
}) => {
  const { conditionGroups, setConditionGroups } = useConditionGroupContext();
  const [type, setType] = useState<
    ApiConditionCustom | ApiSessionConditionCustom | undefined
  >(ApiConditionCustom.UNSPECIFIED);

  const [colWidths, setColWidths] = useState<GridSize[]>(defaultColWidth);

  // todo: work out type
  const condition: any = useMemo(() => {
    return (conditionGroups[groupIdx]?.conditions || [])[rowIdx] || {};
  }, [conditionGroups, groupIdx, rowIdx]);

  useEffect(() => {
    setType(condition.custom || undefined);
  }, [condition]);

  const value = useMemo(() => {
    return (conditionGroups[groupIdx]?.conditions || [])[rowIdx]?.value || "";
  }, [conditionGroups, groupIdx, rowIdx]);

  const operator = useMemo(() => {
    return (
      (conditionGroups[groupIdx]?.conditions || [])[rowIdx]?.operator ||
      ApiOperator.IS_GREATER_THAN_OR_EQUAL
    );
  }, [conditionGroups, groupIdx, rowIdx]);

  const conversionId = useMemo(() => {
    return condition?.conversionId || "";
  }, [condition]);

  const typeIsConversion = useMemo(() => {
    return (
      type &&
      [
        ApiConditionCustom.CONVERSION_TRIGGERED,
        ApiSessionConditionCustom.CONVERSION_TRIGGERED,
      ].includes(type)
    );
  }, [type]);

  useEffect(() => {
    if (typeIsConversion) {
      setColWidths([6, 6]);
    } else {
      setColWidths(defaultColWidth);
    }
  }, [typeIsConversion]);

  return (
    <>
      <ColsLayout spacing={3}>
        <ColsCol width={colWidths[0]} noborder>
          <TextField
            select
            variant="outlined"
            fullWidth
            label="Type"
            value={type || ""}
            onChange={(e) => {
              // setType(e.target.value);
              setConditionGroups(
                produce(conditionGroups, (draft: ApiConditionGroup[]) => {
                  (draft[groupIdx]?.conditions || [])[rowIdx].custom = e.target
                    .value as ApiConditionCustom;
                })
              );
            }}
          >
            {types
              .sort((a, b) =>
                (engagementRuleType(a) || "").localeCompare(
                  engagementRuleType(b) || ""
                )
              )
              .map((t) => (
                <MenuItem key={t} value={t}>
                  {engagementRuleType(t)}
                </MenuItem>
              ))}
          </TextField>
        </ColsCol>
        {!typeIsConversion && (
          <ColsCol width={colWidths[1]} noborder>
            <TextField
              select
              label="Operator"
              fullWidth
              // onMouseOver={() => !selectIsOpen && setColWidths([3, 6, 3])}
              // onMouseOut={() => !selectIsOpen && setColWidths(defaultColWidth)}
              value={operator}
              onChange={(e) => {
                setConditionGroups(
                  produce(conditionGroups, (draft: ApiConditionGroup[]) => {
                    (draft[groupIdx]?.conditions || [])[rowIdx].operator = e
                      .target.value as ApiOperator;
                  })
                );
              }}
            >
              {[
                ApiOperator.IS_GREATER_THAN_OR_EQUAL,
                ApiOperator.IS_GREATER_THAN,
                ApiOperator.IS_LESS_THAN_OR_EQUAL,
                ApiOperator.IS_LESS_THAN,
                ApiOperator.EQUAL,
                ApiOperator.NOT_EQUAL,
              ].map((o) => (
                <MenuItem key={o} value={o}>
                  {formatEnumVal(o)}
                </MenuItem>
              ))}
            </TextField>
          </ColsCol>
        )}
        <ColsCol width={colWidths[colWidths.length - 1]} noborder>
          {typeIsConversion ? (
            <ConversionsDropdown
              label="Conversion"
              variant="outlined"
              fullWidth
              value={conversionId}
              disableClearable
              onChange={(id) => {
                setConditionGroups(
                  produce(conditionGroups, (draft: ApiConditionGroup[]) => {
                    (draft[groupIdx]?.conditions || [])[rowIdx].conversionId =
                      id;
                  })
                );
              }}
            />
          ) : (
            <TextField
              label="value"
              value={value}
              fullWidth
              onChange={(e) => {
                setConditionGroups(
                  produce(conditionGroups, (draft: ApiConditionGroup[]) => {
                    (draft[groupIdx]?.conditions || [])[rowIdx].value =
                      e.target.value;
                  })
                );
              }}
              type={"number"}
              InputProps={{
                startAdornment: [
                  ApiConditionCustom.TIME_ON_PAGE_PERCENTILE,
                  ApiConditionCustom.SCROLL_RATE_PERCENTILE,
                  ApiConditionCustom.VIEW_RATE_PERCENTILE,
                ].includes(type as ApiConditionCustom) ? (
                  <InputAdornment position="start">%</InputAdornment>
                ) : (
                  ""
                ),
              }}
            />
          )}
        </ColsCol>
      </ColsLayout>
    </>
  );
};

function EffectivenessRules({ types }: { types: string[] }) {
  const { setRowComponent } = useConditionGroupContext();

  useEffect(() => {
    setRowComponent(() => (groupIdx, rowIdx) => {
      return <Row types={types} groupIdx={groupIdx} rowIdx={rowIdx} />;
    });
  }, []);

  return (
    <Box>
      <ConditionalGroup />
    </Box>
  );
}

export default EffectivenessRules;
